/**
 * Site-wide behaviour: page scroll lock, drawers, the header's submenus and
 * horizontal tracks. Loaded as a module on every page. Everything is a custom
 * element, so it wires itself up whenever its markup appears — including when
 * the theme editor re-renders a section.
 */

/* ------------------------------------------------------------ scroll lock
   Locks are counted, so a modal opened over a drawer does not unlock the page
   when it closes while the drawer is still open. */
let scrollLocks = 0;

export function lockPageScroll() {
  scrollLocks += 1;
  if (scrollLocks === 1) document.documentElement.classList.add('is-scroll-locked');

  let released = false;
  return () => {
    if (released) return;
    released = true;
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) document.documentElement.classList.remove('is-scroll-locked');
  };
}

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------------------------------------------------------------- drawer
   <ziva-drawer id="…"> wraps a <dialog class="drawer">. Any element with
   data-drawer-open="<id>" opens it. The native dialog gives focus trapping,
   Escape and inertness of the page behind; this adds the slide, the scroll
   lock, closing on the scrim, and returning focus to the opener. */
class ZivaDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.abort = new AbortController();
    const {signal} = this.abort;

    this.dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.close();
      },
      {signal},
    );
    // A click that lands on the dialog itself landed on the scrim.
    this.dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog) this.close();
      },
      {signal},
    );
    this.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      },
      {signal},
    );
    // Whoever closes the dialog (this element, or an app such as EasySell
    // closing every dialog before it opens its own form), the page is let go.
    this.dialog.addEventListener('close', () => this.closed(), {signal});
    // Escape is also heard directly: a close watcher another script opens
    // takes the browser's own Escape before the dialog gets its `cancel`.
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.isOpen && !event.defaultPrevented) this.close();
      },
      {signal},
    );
  }

  disconnectedCallback() {
    this.abort?.abort();
    if (this.dialog?.open) this.dialog.close();
    this.unlock?.();
  }

  get isOpen() {
    return Boolean(this.dialog?.open);
  }

  open(opener) {
    if (!this.dialog || this.dialog.open) return;
    this.opener = opener ?? document.activeElement;
    this.unlock = lockPageScroll();
    this.dialog.showModal();
    this.opener?.setAttribute?.('aria-expanded', 'true');
    // Next frame, so the closed state has painted and the panel slides.
    requestAnimationFrame(() => this.dialog.classList.add('is-open'));
    this.dispatchEvent(new CustomEvent('drawer:open', {bubbles: true}));
  }

  close() {
    if (!this.dialog?.open || this.closing) return;
    this.closing = true;
    this.dialog.classList.remove('is-open');

    const finish = () => {
      clearTimeout(timer);
      this.returnFocus = true;
      this.dialog.close();
    };
    const panel = this.dialog.querySelector('.drawer__panel');
    // transitionend never fires when motion is reduced; the timer covers it.
    const timer = setTimeout(finish, prefersReducedMotion() ? 0 : 400);
    panel?.addEventListener('transitionend', (event) => {
      if (event.target === panel) finish();
    }, {once: true});
  }

  closed() {
    this.dialog.classList.remove('is-open');
    this.closing = false;
    this.unlock?.();
    this.unlock = undefined;
    this.opener?.setAttribute?.('aria-expanded', 'false');
    // Focus goes back only when the drawer closed itself; another dialog
    // taking over keeps its own focus.
    if (this.returnFocus && this.opener?.isConnected) this.opener.focus({preventScroll: true});
    this.returnFocus = false;
    this.dispatchEvent(new CustomEvent('drawer:close', {bubbles: true}));
  }
}

customElements.define('ziva-drawer', ZivaDrawer);

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-drawer-open]');
  if (!opener) return;
  const drawer = document.getElementById(opener.dataset.drawerOpen);
  if (!(drawer instanceof ZivaDrawer)) return;
  // A modified or middle click on a link asks for the page in a new tab.
  if (opener.tagName === 'A' && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
  event.preventDefault();
  drawer.open(opener);
});

/* -------------------------------------------------------------- submenu
   <ziva-submenu> holds a trigger button and a panel of links. It opens on
   hover (fine pointers), focus and click; arrow keys move through the links;
   Escape closes and returns focus to the trigger. */
class ZivaSubmenu extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[aria-controls]');
    this.abort = new AbortController();
    const {signal} = this.abort;
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.trigger?.addEventListener('click', () => this.toggle(!this.isOpen), {signal});
    this.addEventListener('pointerenter', () => finePointer.matches && this.toggle(true), {signal});
    this.addEventListener('pointerleave', () => finePointer.matches && this.toggle(false), {signal});
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.toggle(false);
    }, {signal});
    this.addEventListener('keydown', (event) => this.onKeyDown(event), {signal});
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  toggle(open) {
    this.classList.toggle('is-open', open);
    this.trigger?.setAttribute('aria-expanded', String(open));
  }

  onKeyDown(event) {
    const links = [...this.querySelectorAll('[role="menuitem"]')];
    const index = links.indexOf(document.activeElement);

    if (event.key === 'Escape' && this.isOpen) {
      event.stopPropagation();
      this.toggle(false);
      this.trigger?.focus();
      return;
    }

    let next = null;
    if (event.key === 'ArrowDown') next = index < 0 ? 0 : (index + 1) % links.length;
    else if (event.key === 'ArrowUp') next = index < 0 ? links.length - 1 : (index - 1 + links.length) % links.length;
    else if (event.key === 'Home' && index >= 0) next = 0;
    else if (event.key === 'End' && index >= 0) next = links.length - 1;
    if (next === null || !links.length) return;

    event.preventDefault();
    this.toggle(true);
    links[next].focus();
  }
}

customElements.define('ziva-submenu', ZivaSubmenu);

/* ---------------------------------------------------------------- track
   <ziva-track> drives a horizontal snap row: arrows step by one item and
   the dots mark and jump to the item in view. The row itself stays a plain
   scrollable element, so touch and trackpad scrolling need no script. */
class ZivaTrack extends HTMLElement {
  connectedCallback() {
    this.row = this.querySelector('.ziva-track');
    if (!this.row) return;
    this.abort = new AbortController();
    const {signal} = this.abort;

    this.querySelector('[data-track-prev]')?.addEventListener('click', () => this.step(-1), {signal});
    this.querySelector('[data-track-next]')?.addEventListener('click', () => this.step(1), {signal});
    this.querySelectorAll('[data-track-dot]').forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(Number(dot.dataset.trackDot)), {signal});
    });
    this.row.addEventListener('scroll', () => this.update(), {signal, passive: true});
    window.addEventListener('resize', () => this.update(), {signal, passive: true});
    this.update();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  get items() {
    return [...this.row.children];
  }

  currentIndex() {
    const left = this.row.scrollLeft;
    let best = 0;
    let bestDistance = Infinity;
    this.items.forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft - this.row.offsetLeft - left);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  goTo(index) {
    const item = this.items[index];
    if (!item) return;
    this.row.scrollTo({
      left: item.offsetLeft - this.row.offsetLeft,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  step(direction) {
    this.goTo(Math.max(0, Math.min(this.items.length - 1, this.currentIndex() + direction)));
  }

  update() {
    const index = this.currentIndex();
    const maxScroll = this.row.scrollWidth - this.row.clientWidth - 1;
    const prev = this.querySelector('[data-track-prev]');
    const next = this.querySelector('[data-track-next]');
    if (prev) prev.disabled = this.row.scrollLeft <= 0;
    if (next) next.disabled = this.row.scrollLeft >= maxScroll;
    this.querySelectorAll('[data-track-dot]').forEach((dot) => {
      dot.setAttribute('aria-current', String(Number(dot.dataset.trackDot) === index));
    });
  }
}

customElements.define('ziva-track', ZivaTrack);

/* ----------------------------------------------------------- in view
   <ziva-in-view hidden> marks its parent with data-in-view="true|false", so
   a CSS loop (a slow photo drift, say) only runs while it is on screen. */
class ZivaInView extends HTMLElement {
  connectedCallback() {
    // Theme settings › Animasyonlar can turn the reveal off; then the
    // section is shown in its finished state right away.
    if (document.body.dataset.animations === 'off') {
      this.parentElement?.setAttribute('data-in-view', 'true');
      return;
    }
    const target = this.parentElement;
    if (!target) return;
    this.observer = new IntersectionObserver(([entry]) => {
      target.dataset.inView = entry.isIntersecting ? 'true' : 'false';
    });
    this.observer.observe(target);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

customElements.define('ziva-in-view', ZivaInView);

/* ------------------------------------------------------- accordion group
   <ziva-accordion-group> animates the <details> inside it open and shut to
   their own height and keeps one open at a time. The details work without it;
   this only adds the motion and the exclusivity. */
class ZivaAccordionGroup extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.addEventListener(
      'click',
      (event) => {
        const summary = event.target.closest('summary');
        const details = summary?.parentElement;
        if (!details || details.tagName !== 'DETAILS' || !this.contains(details)) return;
        event.preventDefault();
        // Mid-animation, dataset.open holds where the panel is heading.
        const current = details.dataset.open ? details.dataset.open === 'true' : details.open;
        const open = !current;
        if (open) {
          this.querySelectorAll('details').forEach((other) => {
            if (other !== details && other.open) this.setOpen(other, false, true);
          });
        }
        this.setOpen(details, open);
      },
      {signal: this.abort.signal},
    );
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  setOpen(details, open, immediate = false) {
    const summary = details.querySelector('summary');
    details.animation?.cancel();
    details.dataset.open = String(open);

    const start = details.getBoundingClientRect().height;
    if (open) details.open = true;
    const end = open ? details.scrollHeight : summary.getBoundingClientRect().height;

    if (immediate || prefersReducedMotion()) {
      details.open = open;
      delete details.dataset.open;
      return;
    }

    // height, because a disclosure has no transform equivalent; 240ms on the
    // storefront's ease-out, with dropdowns rather than page transitions.
    details.style.overflow = 'hidden';
    details.animation = details.animate(
      {height: [`${start}px`, `${end}px`]},
      {duration: 240, easing: getComputedStyle(details).getPropertyValue('--z-ease-out').trim() || 'ease-out'},
    );
    const cleanup = () => {
      details.style.removeProperty('overflow');
      details.animation = null;
    };
    details.animation.onfinish = () => {
      if (!open) details.open = false;
      delete details.dataset.open;
      cleanup();
    };
    details.animation.oncancel = cleanup;
  }
}

customElements.define('ziva-accordion-group', ZivaAccordionGroup);

/* ------------------------------------------------------ predictive search
   <ziva-predictive-search> is the search drawer's panel (sections/header.liquid);
   the <ziva-drawer> around it opens and closes it like the cart. Typing asks
   Shopify's /search/suggest for the predictive-search section and swaps it
   in; while the field is empty the drawer shows its suggested products
   instead. Without script the search icons are plain links to the search
   page. */
class ZivaPredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    this.suggestions = this.querySelector('[data-search-suggestions]');
    this.clear = this.querySelector('[data-search-clear]');
    if (!this.input || !this.results) return;

    this.abort = new AbortController();
    const {signal} = this.abort;
    let timer;

    this.input.addEventListener(
      'input',
      () => {
        this.syncClear();
        clearTimeout(timer);
        timer = setTimeout(() => this.search(), 200);
      },
      {signal},
    );
    this.clear?.addEventListener(
      'click',
      () => {
        clearTimeout(timer);
        this.input.value = '';
        this.syncClear();
        this.search();
        this.input.focus();
      },
      {signal},
    );
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.request?.abort();
  }

  syncClear() {
    if (this.clear) this.clear.hidden = !this.input.value;
  }

  async search() {
    const term = this.input.value.trim();
    if (term === (this.lastTerm ?? '')) return;
    this.lastTerm = term;
    this.request?.abort();
    if (this.suggestions) this.suggestions.hidden = Boolean(term);
    if (!term) {
      this.results.innerHTML = '';
      return;
    }

    this.request = new AbortController();
    this.results.innerHTML = `<div class="ziva-skeleton ziva-skeleton--search" role="status" aria-label="${this.dataset.loadingLabel}"></div>`;
    const params = new URLSearchParams({
      q: term,
      section_id: 'predictive-search',
      'resources[type]': 'product,collection,page,article',
      'resources[limit]': '6',
    });
    try {
      const response = await fetch(`${this.dataset.url}?${params}`, {signal: this.request.signal});
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('#shopify-section-predictive-search');
      this.results.innerHTML = section ? section.innerHTML : '';
    } catch (error) {
      if (error.name === 'AbortError') return;
      // A failed request leaves the suggestions in place of empty results;
      // the next keystroke asks again.
      this.lastTerm = undefined;
      this.results.innerHTML = '';
      if (this.suggestions) this.suggestions.hidden = false;
    }
  }
}

customElements.define('ziva-predictive-search', ZivaPredictiveSearch);

/* -------------------------------------------------------------- load more
   <ziva-load-more> turns a paginated list's "next page" link into an
   in-place append: it fetches the next page of the same section through the
   Section Rendering API and moves the new items into the list. The link
   still works as a plain link without script. */
class ZivaLoadMore extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const link = event.target.closest('[data-load-more-next]');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.loadMore(link);
    });
  }

  async loadMore(link) {
    if (this.loading) return;
    this.loading = true;
    const label = link.innerHTML;
    link.setAttribute('aria-busy', 'true');
    link.textContent = link.dataset.loadingLabel;

    try {
      const url = new URL(link.href);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const html = await fetch(url).then((response) => response.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('ziva-load-more');
      const list = this.querySelector('[data-load-more-list]');
      const items = next?.querySelector('[data-load-more-list]')?.children;
      if (!list || !items) throw new Error('No items');

      const firstNew = items[0];
      list.append(...items);
      const nextLink = next.querySelector('[data-load-more-next]');
      if (nextLink) link.replaceWith(nextLink);
      else link.remove();
      // Keep every other parameter of the current address; only the page moves.
      const address = new URL(window.location.href);
      address.searchParams.set('page', url.searchParams.get('page') ?? '');
      history.replaceState(history.state, '', address);
      // Keyboard users land on the first new item rather than back at the top.
      const target = firstNew?.matches('a[href]') ? firstNew : firstNew?.querySelector('a[href]:not([tabindex="-1"])');
      target?.focus({preventScroll: true});
    } catch {
      // A failed append falls back to the page the link points at.
      window.location.href = link.href;
      return;
    } finally {
      this.loading = false;
    }
    link.innerHTML = label;
    link.removeAttribute('aria-busy');
  }
}

customElements.define('ziva-load-more', ZivaLoadMore);

/* A folded review opens and closes in place ("Devamını oku"). */
document.addEventListener('click', (event) => {
  const button = event.target.closest('.ziva-review-card__more');
  if (!button) return;
  const text = document.getElementById(button.getAttribute('aria-controls'));
  if (!text) return;
  const open = button.getAttribute('aria-expanded') !== 'true';
  button.setAttribute('aria-expanded', String(open));
  button.textContent = open ? button.dataset.less : button.dataset.more;
  text.dataset.open = String(open);
  if (open) text.dataset.expanded = 'true';
  else delete text.dataset.expanded;
});

/* Firefox ignores -webkit-user-drag, so pictures inside links, buttons and
   rows are kept from being dragged off the page here. */
document.addEventListener('dragstart', (event) => {
  if (
    event.target instanceof HTMLImageElement &&
    event.target.closest('a, button, .ziva-track, [class*="carousel"], [class*="gallery"]')
  ) {
    event.preventDefault();
  }
});

/* ------------------------------------------------------------- signature
   What a developer sees on opening the console. */
console.info(
  '%cShopimaster%c\nZiva Parfum · theme crafted by Shopimaster · https://shopimaster.com',
  'font: 400 26px/1.3 Gambarino, Georgia, serif; color: #cfab4c; letter-spacing: -0.02em;',
  'font: 12px/1.6 system-ui, sans-serif; color: #8a857d;',
);
